import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { GraduationCap, Check, X as XIcon, Loader2, ChevronDown } from "lucide-react";
import { api } from "../lib/api";
import { TRAINING_LEVELS, TRAINING_SUB_TIERS, TRAINING_LEVEL_ACCENT, trainingLabel } from "../lib/training";
import { useAuth, isAdmin } from "../contexts/AuthContext";

/** Small pill badge showing the player's training placement. */
export function TrainingLevelPill({ level, subTier, size = "md", compact = false, className = "", testId }) {
  const accent = TRAINING_LEVEL_ACCENT[level] || "#575757";
  const text = trainingLabel(level, subTier, { compact });
  const sizeCls = size === "sm"
    ? "text-[9px] px-1.5 py-0.5 tracking-[0.2em]"
    : "text-[10px] px-2 py-1 tracking-[0.25em]";
  return (
    <span
      className={`inline-flex items-center gap-1 uppercase font-bold border ${sizeCls} ${className}`}
      style={{ color: accent, borderColor: `${accent}66`, background: `${accent}12` }}
      data-testid={testId}
    >
      <GraduationCap className={size === "sm" ? "w-2.5 h-2.5" : "w-3 h-3"} />
      {text}
    </span>
  );
}

/** Inline dropdown editor that persists on select. Admins only. */
export function TrainingLevelEditor({ playerId, level, subTier, onChange, className = "" }) {
  const { user } = useAuth();
  const canEdit = isAdmin(user);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pos, setPos] = useState(null);   // { top, right } — viewport-relative
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const accent = TRAINING_LEVEL_ACCENT[level] || "#575757";

  // Recompute menu position whenever it's opened or the viewport changes.
  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (!r) return;
      setPos({ top: r.bottom + 6, right: window.innerWidth - r.right });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  // Close on outside click / escape
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const save = async (nextLevel, nextSub) => {
    setSaving(true);
    try {
      const r = await api.updateTrainingLevel(playerId, {
        training_level: nextLevel,
        training_sub_tier: nextLevel === "Maximize" ? nextSub : null,
      });
      onChange?.(r);
      setOpen(false);
      toast.success("Training placement updated");
    } catch (e) {
      toast.error("Failed to save", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  if (!canEdit) {
    return <TrainingLevelPill level={level} subTier={subTier} className={className} testId="training-level-pill" />;
  }

  return (
    <div className={`relative inline-block ${className}`}>
      <button
        ref={btnRef}
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1 uppercase font-bold border text-[10px] px-2 py-1 tracking-[0.25em] hover:brightness-110"
        style={{ color: accent, borderColor: `${accent}66`, background: `${accent}12` }}
        data-testid="training-level-editor-btn"
      >
        <GraduationCap className="w-3 h-3" />
        {trainingLabel(level, subTier)}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          style={{ position: "fixed", top: pos.top, right: pos.right }}
          className="min-w-[220px] bg-[#141414] border border-white/10 shadow-2xl z-[100] py-1"
          data-testid="training-level-menu"
        >
          <div className="px-3 py-2 text-[9px] uppercase tracking-[0.3em] text-[#757575] border-b border-white/5">Training Placement</div>
          {TRAINING_LEVELS.map(lvl => {
            const isActive = level === lvl;
            const acc = TRAINING_LEVEL_ACCENT[lvl];
            return (
              <div key={lvl}>
                {lvl === "Maximize" ? (
                  <div>
                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: acc }}>Maximize</div>
                    {TRAINING_SUB_TIERS.map(sub => {
                      const active = isActive && subTier === sub;
                      return (
                        <button
                          key={sub}
                          onClick={() => save("Maximize", sub)}
                          disabled={saving}
                          className={`w-full text-left px-6 py-1.5 text-[11px] flex items-center justify-between ${active ? "text-white" : "text-[#a0a0a0] hover:text-white hover:bg-white/5"}`}
                          data-testid={`training-opt-max-${sub.replace(/\s+/g, '-')}`}
                        >
                          <span>{sub}</span>
                          {active && <Check className="w-3.5 h-3.5" style={{ color: acc }} />}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <button
                    onClick={() => save(lvl, null)}
                    disabled={saving}
                    className={`w-full text-left px-3 py-2 text-[11px] uppercase tracking-[0.2em] font-bold flex items-center justify-between ${isActive ? "text-white" : "text-[#a0a0a0] hover:bg-white/5"}`}
                    style={{ color: isActive ? "#ffffff" : undefined }}
                    data-testid={`training-opt-${lvl.replace(/\s+/g, '-')}`}
                  >
                    <span style={{ color: acc }}>{lvl}</span>
                    {isActive && <Check className="w-3.5 h-3.5" style={{ color: acc }} />}
                  </button>
                )}
              </div>
            );
          })}
          <div className="border-t border-white/5 mt-1">
            <button
              onClick={() => save(null, null)}
              disabled={saving}
              className="w-full text-left px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-[#757575] hover:text-red-400 hover:bg-white/5 flex items-center gap-2"
              data-testid="training-opt-clear"
            >
              <XIcon className="w-3 h-3" /> Clear placement
            </button>
          </div>
          {saving && (
            <div className="border-t border-white/5 px-3 py-2 text-[10px] text-[#a0a0a0] flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> Saving…
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
